import { useState, useMemo } from 'react';
import { useAllPaysForMonth, useUpdateInvoiceStatus, useUserPayForMonth } from '../../../lib/supabase/pay/supabasePayService';
import { useUsers } from '../../../lib/supabase/users/supabaseUserService';
import { useSendInvoiceReminderDiscordNotification, useInvoiceReleases, useUpdateInvoiceRelease, useDownloadInvoicePDF, useEmailInvoice } from '../../../lib/supabase/pay/supabasePayService';
import { formatCurrencyGBP } from '../../../utils/format';
import { DataTable } from '../../DataTable';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import {
  FileDown,
  FilePlus,
  RotateCcw,
  Lock,
  Unlock,
  Mail,
  CheckCircle,
  XCircle,
  MoreVertical,
  UserX,
  RefreshCw,
  ChevronDown,
  FileText,
  CheckCircle2,
  FileSpreadsheet,
  Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { toast } from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { NO_PAY_ROLE, TRIAL_ROLE } from '@shiftly/shared/src/constants';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';
import { BulkEmailDeactivatedUsersDialog } from '../../ui/dialogs/bulk-email-deactivated-users-dialog';
import { ConfirmDialog } from '../../ui/dialogs/confirm-dialog';
import { CsvInvoiceComparisonDialog } from '../../ui/dialogs/csv-invoice-comparison-dialog';
import { useCyclePayForm, usePayForm } from '../../../lib/supabase/pay/supabasePayFormService';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { DateTime } from 'luxon';

interface UserInvoice {
  id: number;
  user_id: string;
  userName: string;
  salesEarnings: number;
  schedulingEarnings: number;
  ggSwapEarnings: number;
  otherEarnings: number;
  totalEarnings: number;
  hasInvoice: boolean;
  submitted: boolean;
  submitted_at?: DateTime;
}

interface InvoicesViewProps {
  defaultMonth: number;
  defaultYear: number;
}


export function InvoicesView({ defaultMonth, defaultYear }: InvoicesViewProps) {
  // Fetch data
  const { permissions } = useAuth();
  const { data: pays, isLoading: isPaysLoading } = useAllPaysForMonth(defaultYear, defaultMonth);
  const { data: users, isLoading: isUsersLoading } = useUsers();
  const { mutate: cyclePayForm, isPending: isCyclingPayForm } = useCyclePayForm();
  const { data: payForm, isLoading: isPayFormLoading } = usePayForm();
  const { mutate: updateInvoiceStatus } = useUpdateInvoiceStatus();
  const { mutate: sendManualInvoiceReminder } = useSendInvoiceReminderDiscordNotification();

  const handleBulkSubmitInvoices = async () => {
    if (process.env.NODE_ENV !== 'development') {
      toast.error('This feature is only available in development mode');
      return;
    }

    const eligibleInvoices = userInvoices.filter(invoice => invoice.hasInvoice && !invoice.submitted);

    if (eligibleInvoices.length === 0) {
      toast.error('No invoices available for bulk submission');
      return;
    }

    const loadingId = toast.loading(`Submitting ${eligibleInvoices.length} invoices...`);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const invoice of eligibleInvoices) {
        try {
          // Use the invoice ID directly from the invoice data
          if (invoice.id) {
            await new Promise((resolve, reject) => {
              updateInvoiceStatus(
                { invoice_id: invoice.id, status: 'submitted' },
                {
                  onSuccess: () => {
                    successCount++;
                    resolve(void 0);
                  },
                  onError: (error) => {
                    errorCount++;
                    reject(error);
                  }
                }
              );
            });
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error marking invoice as submitted for user ${invoice.userName}:`, error);
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully submitted ${successCount} invoice${successCount > 1 ? 's' : ''}`);
      }

      if (errorCount > 0) {
        toast.error(`Failed to submit ${errorCount} invoice${errorCount > 1 ? 's' : ''}`);
      }
    } catch (error: any) {
      console.error('Error in bulk submit:', error);
      toast.error(error.message || 'Failed to perform bulk submission');
    } finally {
      toast.dismiss(loadingId);
    }
  };

  const handleSendInvoiceReminder = async () => {
    const loadingId = toast.loading('Sending invoice reminders to all users...');

    sendManualInvoiceReminder(
      {year: defaultYear, month: defaultMonth},
      {
        onSuccess: () => {
          toast.success('Invoice reminders sent successfully to all users');
          toast.dismiss(loadingId);
        },
        onError: (error: any) => {
          console.error('Error sending invoice reminders:', error);
          toast.error(error.message || 'Failed to send invoice reminders');
          toast.dismiss(loadingId);
        }
      }
    );
  };

  // Release status
  const { data: invoiceReleases = [], isLoading: isReleaseStatusLoading, refetch: refetchReleaseStatus } = useInvoiceReleases();
  const { mutate: updateInvoiceRelease } = useUpdateInvoiceRelease();
  const [isUpdatingReleaseStatus, setIsUpdatingReleaseStatus] = useState(false);

  const currentReleaseStatus = useMemo(() => {
    const release = invoiceReleases.find(r => r.year === defaultYear && r.month === defaultMonth);
    return release?.released || false;
  }, [invoiceReleases, defaultYear, defaultMonth]);

  // Deactivated users dialog state
  const [isDeactivatedUsersDialogOpen, setIsDeactivatedUsersDialogOpen] = useState(false);
  // Cycle form confirmation dialog state
  const [isConfirmCycleFormOpen, setIsConfirmCycleFormOpen] = useState(false);
  // CSV comparison dialog state
  const [isCsvComparisonDialogOpen, setIsCsvComparisonDialogOpen] = useState(false);

  // Helper function to get month name
  const getMonthName = (month: number) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1];
  };

  const handleCycleForm = async () => {
    const monthName = getMonthName(defaultMonth);
    const formTitle = `${monthName} ${defaultYear} Payment Form`;

    cyclePayForm(formTitle, {
      onSuccess: (data) => {
        if (data?.success) {
          toast.success(`New form created: ${formTitle}`);
          setIsConfirmCycleFormOpen(false);
        } else {
          toast.error(`Failed to create form: ${data?.message || 'Unknown error'}`);
        }
      },
      onError: (error) => {
        toast.error(`Error creating form: ${error.message}`);
      }
    });
  };

  const handleToggleReleaseStatus = async () => {
    setIsUpdatingReleaseStatus(true);
    const newStatus = !currentReleaseStatus;
    const loadingId = toast.loading(`${newStatus ? 'Releasing' : 'Unreleasing'} invoices...`);

    try {
      updateInvoiceRelease(
        { year: defaultYear, month: defaultMonth, released: newStatus },
        {
          onSuccess: () => {
            toast.success(`Invoices ${newStatus ? 'released' : 'unreleased'} successfully`);
            refetchReleaseStatus();
          },
          onError: (error) => {
            console.error('Error updating release status:', error);
            toast.error(`Failed to ${newStatus ? 'release' : 'unrelease'} invoices`);
          },
          onSettled: () => {
            setIsUpdatingReleaseStatus(false);
            toast.dismiss(loadingId);
          }
        }
      );
    } catch (error) {
      console.error('Error updating release status:', error);
      toast.error(`Failed to ${newStatus ? 'release' : 'unrelease'} invoices`);
      setIsUpdatingReleaseStatus(false);
      toast.dismiss(loadingId);
    }
  };

  // Combine data to create invoice entries
  const userInvoices = useMemo(() => {
    const invoices: UserInvoice[] = [];

    if (pays && users) {
      pays.forEach(payData => {
        const user = users.find(u => u.id === payData.invoice.user_id);
        if (user && !user.roles.includes(TRIAL_ROLE) && !user.roles.includes(NO_PAY_ROLE)) {
          const totalEarnings = payData.totals.grand_total || 0;

          // Only include users with earnings
          if (totalEarnings > 0) {
            invoices.push({
              id: payData.invoice.id || -1,
              user_id: payData.invoice.user_id,
              userName: user.full_name || 'Unknown User',
              salesEarnings: payData.totals.sales_total || 0,
              schedulingEarnings: payData.totals.scheduling_total || 0,
              ggSwapEarnings: payData.totals.marketing_total || 0,
              otherEarnings: payData.totals.misc_total + (payData.totals.bonus_total || 0),
              totalEarnings,
              hasInvoice: payData.invoice.id ? true : false,
              submitted: payData.invoice.status === 'submitted',
              submitted_at: payData.invoice.status === 'submitted' && payData.invoice.status_updated_at
                ? DateTime.fromISO(payData.invoice.status_updated_at)
                : undefined
            });
          }
        }
      });
    }

    return invoices.sort((a, b) => a.userName.localeCompare(b.userName));
  }, [pays, users]);

  // Calculate deactivated users with earnings
  const deactivatedUsersWithEarnings = useMemo(() => {
    if (!pays || !users) return [];

    return pays.filter(payData => {
      const user = users.find(u => u.id === payData.invoice.user_id);
      return user && user.deactivated_at && user.email && (payData.totals.grand_total || 0) > 0;
    });
  }, [pays, users]);

  // Table columns - different based on permissions
  const columns: ColumnDef<UserInvoice>[] = useMemo(() => {
    const baseColumns: ColumnDef<UserInvoice>[] = [
      {
        accessorKey: 'userName',
        header: 'Name',
      },
    ];

    // Full columns for users with full permissions
    return [
      ...baseColumns,
      {
        accessorKey: 'salesEarnings',
        header: 'Sales',
        cell: ({ row }) => formatCurrencyGBP(row.original.salesEarnings),
      },
      {
        accessorKey: 'schedulingEarnings',
        header: 'Scheduling',
        cell: ({ row }) => formatCurrencyGBP(row.original.schedulingEarnings),
      },
      {
        accessorKey: 'ggSwapEarnings',
        header: 'GG Swap',
        cell: ({ row }) => formatCurrencyGBP(row.original.ggSwapEarnings),
      },
      {
        accessorKey: 'otherEarnings',
        header: 'Other',
        cell: ({ row }) => formatCurrencyGBP(row.original.otherEarnings),
      },
      {
        accessorKey: 'totalEarnings',
        header: 'Total',
        cell: ({ row }) => (
          <span className="font-medium">
            {formatCurrencyGBP(row.original.totalEarnings)}
          </span>
        ),
      },
      {
        accessorKey: 'submitted',
        header: 'Status',
        cell: ({ row }) => (
          <InvoiceStatusBadge
            submitted={row.original.submitted}
            hasInvoice={row.original.hasInvoice}
            submittedAt={row.original.submitted_at}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const aStatus = rowA.original.submitted ? 1 : 0;
          const bStatus = rowB.original.submitted ? 1 : 0;
          return aStatus - bStatus;
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right pr-2">Actions</div>,
        cell: ({ row }) => {
          return (
            <InvoiceButton
              userId={row.original.user_id}
              month={defaultMonth}
              year={defaultYear}
              userName={row.original.userName}
              hasInvoice={row.original.hasInvoice}
              submitted={row.original.submitted}
              updateInvoiceStatus={updateInvoiceStatus}
            />
          );
        },
      },
    ];
  }, [defaultMonth, defaultYear]);

  const isLoading = isPaysLoading || isUsersLoading;
  const hasPermission = permissions.includes(Permissions.PAY_RELEASE_INVOICES);
  const hasCycleFormPermission = permissions.includes(Permissions.PAY_CYCLE_FORM);

  return (
    <div className="space-y-6">
      {/* Release Status Control */}
      <div className="flex items-center justify-between p-4 bg-brand-dark dark:bg-brand-dark/50 rounded-lg border">
        <div className="space-y-1">
          <h3 className="font-semibold text-brand-dark dark:text-foreground">Invoice Release Status</h3>
          <p className="text-sm text-muted-foreground dark:text-muted-foreground">
            {currentReleaseStatus
              ? 'Invoices are currently released and available to users'
              : 'Invoices are currently unreleased and not available to users'}
          </p>
        </div>
        {hasPermission && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground">
              {currentReleaseStatus ? (
                <>
                  <Unlock className="h-4 w-4 text-brand-green" />
                  Released
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 text-brand-red" />
                  Unreleased
                </>
              )}
            </div>
            <Switch
              checked={currentReleaseStatus}
              onCheckedChange={handleToggleReleaseStatus}
              disabled={isReleaseStatusLoading || isUpdatingReleaseStatus}
            >
            </Switch>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2"
            >
              <span>Actions</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[220px]">
            {permissions.includes(Permissions.PAY_GENERATE_INVOICES) && (
              <>
              <DropdownMenuItem
                  onClick={() => setIsCsvComparisonDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Compare with CSV
                </DropdownMenuItem>
              </>
            )}

            {deactivatedUsersWithEarnings.length > 0 && hasPermission && (
              <>
                <DropdownMenuItem
                  onClick={() => setIsDeactivatedUsersDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <UserX className="h-4 w-4" />
                  Email Deactivated Users
                </DropdownMenuItem>
              </>
            )}

            {process.env.NODE_ENV === 'development' && permissions.includes(Permissions.PAY_GENERATE_INVOICES) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleBulkSubmitInvoices}
                  className="flex items-center gap-2 text-brand-dark dark:text-brand-gold"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Bulk Submit (Dev Only)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSendInvoiceReminder}
                  className="flex items-center gap-2 text-brand-dark dark:text-brand-gold"
                >
                  <Bell className="h-4 w-4" />
                  Send Invoice Reminders (Dev Only)
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Form Management - Keep as separate dropdown since it has its own submenu */}
        {hasCycleFormPermission && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 max-w-full"
                disabled={isPayFormLoading}
              >
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span className="truncate max-w-[200px] lg:max-w-[300px]">
                  {isPayFormLoading ? (
                    'Loading...'
                  ) : payForm ? (
                    'Pay Form'
                  ) : (
                    'No Active Form'
                  )}
                </span>
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[250px]">
              <DropdownMenuItem
                onClick={() => setIsConfirmCycleFormOpen(true)}
                disabled={isCyclingPayForm}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isCyclingPayForm ? 'animate-spin' : ''}`} />
                {isCyclingPayForm ? 'Creating Form...' : 'Cycle Form'}
              </DropdownMenuItem>
              {payForm && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs text-muted-foreground">
                    Form ID: {payForm.id}
                  </div>
                  <div className="px-2 py-1 text-xs text-muted-foreground">
                    Created: {payForm.created_at}
                  </div>
                  {payForm.form_link && (
                    <DropdownMenuItem
                      onClick={() => window.open(payForm.form_link, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Open Form
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <DataTable
        data={userInvoices}
        columns={columns}
        isLoadingMore={isLoading}
        searchPlaceholder="Search users..."
        initialSorting={[{ id: 'userName', desc: false }]}
        getRowUrl={(row) => `/users/${row.user_id}`}
      />

      {/* Deactivated Users Dialog */}
      <BulkEmailDeactivatedUsersDialog
        isOpen={isDeactivatedUsersDialogOpen}
        onClose={() => setIsDeactivatedUsersDialogOpen(false)}
        users={users || []}
        pays={pays || []}
        month={defaultMonth + 1}
        year={defaultYear}
      />

      {/* Cycle Form Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmCycleFormOpen}
        onClose={() => setIsConfirmCycleFormOpen(false)}
        onConfirm={handleCycleForm}
        title="Confirm Cycle Form"
        message={
          <div className="space-y-3">
            <p>This will replace the existing active form and may affect ongoing payment processes. Only do this if you know what you are doing.</p>
          </div>
        }
        confirmText={isCyclingPayForm ? 'Creating...' : 'Cycle Form'}
        cancelText="Cancel"
        loading={isCyclingPayForm}
      />

      {/* CSV Comparison Dialog */}
      <CsvInvoiceComparisonDialog
        isOpen={isCsvComparisonDialogOpen}
        onClose={() => setIsCsvComparisonDialogOpen(false)}
        userInvoices={userInvoices}
        users={users || []}
      />
    </div>
  );
}

// Component to show invoice status badge - now simplified since we pass the data directly
function InvoiceStatusBadge({
  submitted,
  hasInvoice,
  submittedAt
}: {
  submitted: boolean;
  hasInvoice: boolean;
  submittedAt?: DateTime;
}) {
  if (hasInvoice && submitted) {
    const title = submittedAt ? `Submitted on ${submittedAt.toLocaleString(DateTime.DATETIME_MED)}` : 'Submitted';
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge variant="green">Submitted</Badge>
        </TooltipTrigger>
        <TooltipContent>
          {title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return <Badge variant="destructive">Pending</Badge>;
}

// Component to handle invoice button logic separately
function InvoiceButton({
  userId: user_id,
  month,
  year,
  hasInvoice: has_invoice,
  submitted,
  updateInvoiceStatus,
}: {
  userId: string;
  month: number;
  year: number;
  userName?: string;
  hasInvoice: boolean;
  submitted: boolean;
  updateInvoiceStatus: any;
}) {
  const [isMarkingSubmitted, setIsMarkingSubmitted] = useState(false);
  const { permissions } = useAuth();

  // Get the user's pay data to access invoice information
  const { data: userPayData } = useUserPayForMonth(user_id, year, month);
  const downloadInvoicePDF = useDownloadInvoicePDF();
  const emailInvoice = useEmailInvoice();

  const handleInvoiceAction = async () => {
    downloadInvoicePDF.mutate(
      { user_id, year, month: month },
      {
        onSuccess: () => {
          toast.success('Invoice downloaded successfully');
        },
        onError: (error: any) => {
          console.error('Error downloading invoice:', error);
          toast.error(error.message || 'Failed to download invoice');
        }
      }
    );
  };

  const handleEmailInvoice = async () => {
    emailInvoice.mutate(
      { userId: user_id, year, month: month + 1 },
      {
        onSuccess: (result) => {
          toast.success(`Invoice emailed successfully to ${result.userEmail}`);
        },
        onError: (error: any) => {
          console.error('Error emailing invoice:', error);
          toast.error(error.message || 'Failed to email invoice');
        }
      }
    );
  };

  const handleMarkAsSubmitted = async () => {
    if (!permissions.includes(Permissions.PAY_GENERATE_INVOICES)) {
      toast.error('You do not have permission to mark invoices as submitted');
      return;
    }

    setIsMarkingSubmitted(true);
    const loadingId = toast.loading('Marking invoice as submitted...');

    if (!userPayData?.invoice?.id) {
      toast.error('Invoice not found');
      setIsMarkingSubmitted(false);
      toast.dismiss(loadingId);
      return;
    }

    updateInvoiceStatus(
      { invoice_id: userPayData.invoice.id, status: 'submitted' },
      {
        onSuccess: () => {
          toast.success('Invoice marked as submitted successfully');
        },
        onError: (error: any) => {
          console.error('Error marking invoice as submitted:', error);
          toast.error(error.message || 'Failed to mark invoice as submitted');
        },
        onSettled: () => {
          setIsMarkingSubmitted(false);
          toast.dismiss(loadingId);
        }
      }
    );
  };

  const handleMarkAsUnsubmitted = async () => {
    if (!permissions.includes(Permissions.PAY_GENERATE_INVOICES)) {
      toast.error('You do not have permission to unmark invoices as submitted');
      return;
    }

    setIsMarkingSubmitted(true);
    const loadingId = toast.loading('Marking invoice as unsubmitted...');

    if (!userPayData?.invoice?.id) {
      toast.error('Invoice not found');
      setIsMarkingSubmitted(false);
      toast.dismiss(loadingId);
      return;
    }

    updateInvoiceStatus(
      { invoice_id: userPayData.invoice.id, status: 'generated' },
      {
        onSuccess: () => {
          toast.success('Invoice marked as unsubmitted successfully');
        },
        onError: (error: any) => {
          console.error('Error marking invoice as unsubmitted:', error);
          toast.error(error.message || 'Failed to mark invoice as unsubmitted');
        },
        onSettled: () => {
          setIsMarkingSubmitted(false);
          toast.dismiss(loadingId);
        }
      }
    );
  };

  const canManageInvoices = permissions.includes(Permissions.PAY_GENERATE_INVOICES);
  const isSubmitted = submitted;
  const invoiceExists = has_invoice;
  const isAnyLoading = downloadInvoicePDF.isPending || emailInvoice.isPending || isMarkingSubmitted;

  return (
    <div className="flex -space-x-1 justify-end">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={(e) => {
          e.stopPropagation();
          handleInvoiceAction();
        }}
        disabled={isAnyLoading}
        description={invoiceExists ? 'Download Invoice' : 'Generate Invoice'}
      >
        {invoiceExists ? (
          <FileDown className="w-4 h-4 text-brand-green" />
        ) : (
          <FilePlus className="w-4 h-4" />
        )}
      </Button>

      {/* Additional Actions Dropdown */}
      {(canManageInvoices) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 ml-1"
              onClick={(e) => e.stopPropagation()}
              disabled={isAnyLoading}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[180px]">
            {canManageInvoices && (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInvoiceAction();
                  }}
                  disabled={isAnyLoading}
                >
                  <RotateCcw className="mr-2 h-4 w-4 text-brand-gold" />
                  Download
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEmailInvoice();
                  }}
                  disabled={isAnyLoading}
                >
                  <Mail className="mr-2 h-4 w-4 text-brand-gold" />
                  Email Invoice
                </DropdownMenuItem>
              </>
            )}

            {canManageInvoices && (
              <>
                <DropdownMenuSeparator />
                {!isSubmitted ? (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsSubmitted();
                    }}
                    disabled={isAnyLoading}
                  >
                    <CheckCircle className="mr-2 h-4 w-4 text-brand-green" />
                    Mark as Submitted
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsUnsubmitted();
                    }}
                    disabled={isAnyLoading}
                  >
                    <XCircle className="mr-2 h-4 w-4 text-brand-red" />
                    Mark as Unsubmitted
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
} 